import { useState, VFC } from "react";

import { Button } from "@portex-pro/ui-components";
import { ContactType } from "api/rest/partners/types/domain";
import AddPartnerButtonContainer from "app/pages/bid-request/pages/partners/components/AddPartnerButtonContainer";
import PartnerTable from "components/partners/PartnerTable";
import SimpleSearchView from "components/SimpleSearchView";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import StepCollapse from "../../_components/StepCollapse";
import { useCreateStepCollapseProps } from "../../_hooks/useStepCollapse";
import { useQuoteRequestSelector } from "../../_store";
import { quoteRequestActions } from "../../_store/quoteRequestSliceV2";
import { QuoteStepIndex } from "../../types";
import { FinalSchema } from "../_schemas";

type PartnersStepTagProps = {
  selectedIds: Set<string>;
  allPartners: ContactType[];
};

const PartnersStepTag = ({ selectedIds, allPartners }: PartnersStepTagProps) => {
  if (selectedIds.size === 0) {
    return <></>;
  }

  const selectedCompanyNames = allPartners
    .filter((partner) => selectedIds.has(partner.id.toString()))
    .map((partner) => partner.company_name)
    .filter(Boolean);

  const uniqueCompanyNames = Array.from(new Set(selectedCompanyNames));

  if (uniqueCompanyNames.length === 0) {
    return <> </>;
  }

  let displayText = "";
  if (uniqueCompanyNames.length <= 2) {
    displayText = uniqueCompanyNames.join(", ");
  } else {
    const extraCount = selectedIds.size - 2;
    displayText = `${uniqueCompanyNames.slice(0, 2).join(", ")} +${extraCount}`;
  }

  return (
    <div className="inline-flex items-center bg-blue-50 rounded-md px-2 py-1">
      <span className="text-blue-600 font-bold text-sm">{displayText}</span>
    </div>
  );
};

const PartnersStep: VFC<{ onSubmit: (data: FinalSchema) => void }> = ({ onSubmit }) => {
  const { t } = useTranslation(["quoteRequest", "common", "shipper"]);
  const setStepIndex = quoteRequestActions.useSetStepIndex();
  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();
  const { stepIndex, quoteRequest: quoteRequest } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const [search, setSearch] = useState<string>("");
  const [allPartners, setAllPartners] = useState<ContactType[]>([]);
  const { baseStepCollapseProps, setIsOpen } = useCreateStepCollapseProps({
    expectedStepIndex: QuoteStepIndex.Partners,
    name: t("shipper:partners"),
    number: 3,
  });
  const methods = useFormContext<FinalSchema>();

  const { field, fieldState } = useController({ control: methods.control, name: "partners" });

  const submit = (data: FinalSchema) => {
    onSubmit(data);
    setStepIndex(QuoteStepIndex.Team);
    setIsOpen(false);
  };

  return (
    <StepCollapse
      {...baseStepCollapseProps}
      tag={
        <PartnersStepTag
          selectedIds={new Set(field.value.map(({ contact_id }) => contact_id.toString()))}
          allPartners={allPartners}
        />
      }
      footer={
        stepIndex <= QuoteStepIndex.Partners ? (
          <div className="flex justify-between mt-2 w-full items-center">
            {fieldState.error ? <p className="text-sm text-red-600">{fieldState.error.message}</p> : <div />}
            <div className="flex items-center">
              {!!quoteRequest.partners?.length && quoteRequest.partners.length > 0 && (
                <span className="text-sm text-gray-600 pr-2">
                  {t("shipper:partnersSelected", { count: quoteRequest.partners.length })}
                </span>
              )}
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={methods.handleSubmit(submit)}
                style={{ width: 150 }}
              >
                {t("common:next")}
              </Button>
            </div>
          </div>
        ) : null
      }
    >
      <div className="flex w-full mt-6">
        <PartnerTable
          wrapInScrollable
          containerClassName="min-h-[700px] w-full flex flex-col"
          showPagination
          selectedPartnerIds={new Set(field.value.map(({ contact_id }) => contact_id.toString()))}
          actionsSlot={<AddPartnerButtonContainer />}
          searchValue={search}
          onPartnersLoaded={(partners) => {
            setAllPartners((prevPartners) => {
              const partnerMap = new Map(prevPartners.map((p) => [p.id, p]));

              partners.forEach((partner) => {
                partnerMap.set(partner.id, partner);
              });

              return Array.from(partnerMap.values());
            });
          }}
          searchSlot={
            <SimpleSearchView
              search={search ?? ""}
              onChange={(search) => setSearch(search)}
              placeholder={t("shipper:searchPartners")}
            />
          }
          headerSlot={
            <div className="px-4 mb-4">
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                <b>{t("shipper:allPartnersBCCd")}</b>
              </div>
            </div>
          }
          onSelectionChange={(selectedIds) =>
            setQuoteRequestAction({
              partners: Array.from(selectedIds).map((contactId) => ({ contact_id: Number(contactId) })),
            })
          }
        />
      </div>
    </StepCollapse>
  );
};

export default PartnersStep;
